// src/lib/time/user-day.ts
//
// "Today" in the USER'S time zone, for server-side daily-boundary logic (streak
// resets, the daily-drill cap, last-active). Everything here is pure and takes an
// explicit `now`, so it's deterministic and testable. A null/unknown/invalid zone
// falls back to UTC — the previous behavior — so callers are always safe.

// Building an Intl.DateTimeFormat is the expensive part of every conversion
// here (it loads the zone's rules), and this module used to build two per call:
// one in safeZone just to validate the name, one in partsInZone to read the
// clock. One formatter per zone, kept for the life of the process, makes a
// conversion a single formatToParts. A formatter is immutable and its output
// depends only on the instant passed in, so sharing it is safe. The cache is
// bounded by the number of distinct zone names seen (a few hundred IANA zones
// at most; an invalid name is cached as 'UTC' and never builds a formatter).
const ZONE_FORMATTERS = new Map<string, Intl.DateTimeFormat>()
const VALID_ZONE = new Map<string, boolean>()

function zoneFormatter(zone: string): Intl.DateTimeFormat {
  let fmt = ZONE_FORMATTERS.get(zone)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
    ZONE_FORMATTERS.set(zone, fmt)
  }
  return fmt
}

/** Validate an IANA zone; returns it if usable, else 'UTC'. */
export function safeZone(timezone: string | null | undefined): string {
  if (!timezone) return 'UTC'
  let valid = VALID_ZONE.get(timezone)
  if (valid === undefined) {
    try {
      // Throws RangeError on an unknown zone. The formatter is the one the
      // conversions need anyway, so validating builds nothing extra.
      zoneFormatter(timezone)
      valid = true
    } catch {
      valid = false
    }
    VALID_ZONE.set(timezone, valid)
  }
  return valid ? timezone : 'UTC'
}

type Parts = { y: number; mo: number; d: number; h: number; mi: number; s: number }

/** Wall-clock parts of `now` as seen in `zone`. */
function partsInZone(now: Date, zone: string): Parts {
  const map: Record<string, string> = {}
  for (const p of zoneFormatter(zone).formatToParts(now)) map[p.type] = p.value
  return {
    y: Number(map.year),
    mo: Number(map.month),
    d: Number(map.day),
    // h23 can emit '24' for midnight on some engines — normalize to 0.
    h: Number(map.hour) % 24,
    mi: Number(map.minute),
    s: Number(map.second),
  }
}

/** The user's local calendar date as `YYYY-MM-DD` for `now`. */
export function userDateString(
  timezone: string | null | undefined,
  now: Date = new Date(),
): string {
  const p = partsInZone(now, safeZone(timezone))
  const mm = String(p.mo).padStart(2, '0')
  const dd = String(p.d).padStart(2, '0')
  return `${p.y}-${mm}-${dd}`
}

/** The user's local date `YYYY-MM-DD` for the day before `now`. */
export function userYesterdayString(
  timezone: string | null | undefined,
  now: Date = new Date(),
): string {
  // Step back a full day from the start-of-local-day instant, then re-read the
  // local date — correct across month/year boundaries and (approximately) DST.
  const startToday = startOfUserDayUtc(timezone, now)
  const prior = new Date(startToday.getTime() - 12 * 60 * 60 * 1000)
  return userDateString(timezone, prior)
}

/**
 * The UTC instant at the start (00:00 local) of the user's day containing `now`.
 * Computed by subtracting the time already elapsed since local midnight from the
 * known UTC instant of `now`, so it's correct regardless of the zone's offset.
 */
export function startOfUserDayUtc(
  timezone: string | null | undefined,
  now: Date = new Date(),
): Date {
  const p = partsInZone(now, safeZone(timezone))
  const elapsedMs = ((p.h * 60 + p.mi) * 60 + p.s) * 1000
  return new Date(now.getTime() - elapsedMs)
}

/**
 * The UTC instant at the start (00:00 local) of the FIRST day of the user's
 * current local month — the boundary for per-user MONTHLY windows (the monthly
 * test cap, the LLM-spend quota). Null/unknown zone falls back to the UTC month
 * start (the previous behavior).
 *
 * Built by stepping back to the 1st of the local month and re-flooring to that
 * day's local midnight, so it's DST-correct (the noon cushion absorbs the ≤1h
 * a DST transition could shift the date).
 */
export function startOfUserMonthUtc(
  timezone: string | null | undefined,
  now: Date = new Date(),
): Date {
  const startToday = startOfUserDayUtc(timezone, now)
  const p = partsInZone(now, safeZone(timezone))
  // (p.d - 1) whole days back lands near the 1st; +12h keeps a DST shift from
  // flipping us onto the previous month before the final floor.
  const nearFirst = new Date(
    startToday.getTime() - (p.d - 1) * 24 * 60 * 60 * 1000 + 12 * 60 * 60 * 1000,
  )
  return startOfUserDayUtc(timezone, nearFirst)
}

/**
 * A HUMAN-READABLE date in the user's own zone — "Aug 5, 2026".
 *
 * `userDateString` returns YYYY-MM-DD for keys and comparisons; this is for
 * anything a person reads.
 *
 * WHY IT EXISTS: server components were calling
 * `date.toLocaleDateString('en-US', { dateStyle: 'medium' })` with no
 * `timeZone`, which resolves to the SERVER's zone — UTC on Vercel. A student in
 * New York finishing a test at 10:49 PM on Aug 5 is 02:49 UTC on Aug 6, so
 * every evening test in the Americas was labelled with tomorrow's date. Nine of
 * the completed attempts in production fall in that 00:00-08:00 UTC window.
 *
 * It reads correctly in local development precisely because the developer's
 * machine is NOT in UTC, which is what let this survive.
 */
export function userDateLabel(
  timezone: string | null | undefined,
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { ...options, timeZone: safeZone(timezone) })
}

/** Same, with a time — "Aug 5, 2026, 10:49 PM". */
export function userDateTimeLabel(
  timezone: string | null | undefined,
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', { ...options, timeZone: safeZone(timezone) })
}

/**
 * The LAST calendar day (YYYY-MM-DD, in `timezone`) covered by a period whose
 * end is EXCLUSIVE — access stops AT `exclusiveEnd`, so the last covered moment
 * is one millisecond before it.
 *
 * WHY IT EXISTS: a school license's expiresAt is 00:00 school time on the day
 * AFTER its last day (src/lib/school/license-dates.ts), because every check is
 * `now < expiresAt`. Printing that instant itself names the day AFTER the last
 * day — the dashboard said "until October 15" while /school said "through
 * October 14" for the same license (external QA, 2026-10). Reading the day that
 * holds end-1ms is the date the enforcement actually honours.
 *
 * Invalid date → '' (callers use this as a key; '' never matches a real day).
 */
export function lastCoveredDateString(
  timezone: string | null | undefined,
  exclusiveEnd: Date | string | number,
): string {
  const end = exclusiveEnd instanceof Date ? exclusiveEnd : new Date(exclusiveEnd)
  if (Number.isNaN(end.getTime())) return ''
  return userDateString(timezone, new Date(end.getTime() - 1))
}

/**
 * The same last covered day, for a person to read — "Oct 14, 2026" — always
 * worded "through {label}". Never "until {label}": "until" + the exclusive end
 * reads as one day longer than access really lasts.
 *
 * Invalid date → '—', like userDateLabel.
 */
export function lastCoveredDayLabel(
  timezone: string | null | undefined,
  exclusiveEnd: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const end = exclusiveEnd instanceof Date ? exclusiveEnd : new Date(exclusiveEnd)
  if (Number.isNaN(end.getTime())) return '—'
  return userDateLabel(timezone, new Date(end.getTime() - 1), options)
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/

/** True for a real calendar date written YYYY-MM-DD ('2026-02-30' is not one). */
export function isCalendarDate(ymd: unknown): ymd is string {
  if (typeof ymd !== 'string') return false
  const m = YMD.exec(ymd)
  if (!m) return false
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  // Date.UTC rolls Feb 30 into March and maps years 0-99 to 1900-1999, so a
  // date is real only if it reads back unchanged.
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === ymd
}

/** `zone`'s offset from UTC at instant `at`, in ms (local wall clock − UTC). */
function zoneOffsetMs(zone: string, at: number): number {
  const whole = Math.floor(at / 1000) * 1000 // partsInZone reads whole seconds
  const p = partsInZone(new Date(whole), zone)
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - whole
}

/**
 * The UTC instant at 00:00 in `timezone` on calendar date `ymd` (YYYY-MM-DD)
 * plus `dayOffset` days — the moment that local day starts. Null if `ymd` isn't
 * YYYY-MM-DD. A null/invalid zone is UTC, like everything here.
 *
 * Day overflow rolls over like Date.UTC ('2026-02-30' is Mar 2): that is what
 * lets `dayOffset` step across a month end. Check input a person typed with
 * isCalendarDate first (exclusiveEndForLastDay does).
 *
 * HOW: read the zone's offset at the target wall time, step back by it, then
 * re-read the offset at that guess — a DST change between the two moves it
 * once, so two reads settle every zone. Where midnight doesn't exist (clocks
 * jump from 23:59 to 01:00 — Santiago, Havana, Cairo) the day starts at the
 * jump, the later guess. The school-license version this replaces floored
 * noon UTC to local midnight, which names the wrong day in zones east of
 * UTC+12 (Auckland: noon UTC is already tomorrow there).
 */
export function startOfLocalDateUtc(
  timezone: string | null | undefined,
  ymd: string,
  dayOffset = 0,
): Date | null {
  const m = YMD.exec(ymd)
  if (!m) return null
  const zone = safeZone(timezone)
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + dayOffset)
  if (Number.isNaN(wall)) return null
  const target = new Date(wall).toISOString().slice(0, 10)
  const first = wall - zoneOffsetMs(zone, wall)
  const second = wall - zoneOffsetMs(zone, first)
  for (const t of [Math.min(first, second), Math.max(first, second)]) {
    if (userDateString(zone, new Date(t)) === target) return new Date(t)
  }
  return new Date(second)
}

/**
 * The EXCLUSIVE end for access whose LAST day is `lastDay` (YYYY-MM-DD,
 * inclusive) in `timezone`: 00:00 there on the day after. Null unless `lastDay`
 * is a real calendar date.
 *
 * The inverse of lastCoveredDateString — for every zone and day,
 * lastCoveredDateString(tz, exclusiveEndForLastDay(tz, d)) === d — so the day
 * an admin picks is the day every "through {date}" label prints, and checks
 * of the form `now < end` keep access for the whole of that day.
 */
export function exclusiveEndForLastDay(
  timezone: string | null | undefined,
  lastDay: string,
): Date | null {
  if (!isCalendarDate(lastDay)) return null
  return startOfLocalDateUtc(timezone, lastDay, 1)
}
